import { IconButton } from "@/subframe/components/IconButton";
import { TextField } from "@/subframe/components/TextField";
import { Button } from "@/subframe/components/Button";
import { DropdownMenu } from "@/subframe/components/DropdownMenu";
import * as SubframeCore from "@subframe/core";
import { ChatList } from "@/subframe/components/ChatList";
import { ActionCard } from "@/subframe/components/ActionCard";
import Friend from "./Friend";
import ChatRoom from "./ChatRoom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addSelectedLabel,
  getConversation,
  getRecipients,
  getScheduledMessages,
  getLegacyScheduledMessages,
  newMessageByWebhook,
  newRecipientWithMessage,
  removeSelectedLabel,
  resetRecipients,
  setConversationStatus,
  setCurrentRecipient,
  clearScheduledMessages,
  clearLegacyScheduledMessages,
  updateMessageStatus,
  updateRecipient,
} from "../redux/reducers/messengerSlice";
import { AppDispatch, RootState } from "../redux/store";
import { Item } from "../redux/interfaces/reducersInterfaces";
import {
  fetchLabels,
  selectRole,
  selectUserLabels,
} from "../redux/reducers/authSlice";
import Settings from "./Settings";
import LabelCreation from "./subcomponents/LabelCreation";
import DeleteLabelPopup from "./subcomponents/DeleteLabelPopup";
import SwitchToAiAssistantPopup from "./subcomponents/SwitchToAiAssistantPopup";
// import SelectDateAndTimePopup from "./subcomponents/SelectDateAndTimePopup";
import { io, Socket } from "socket.io-client";
import { Message } from "../Interfaces/messagesInterface";
import SwitchToHumanAgent from "./subcomponents/SwitchToHunamAgentPopup";
import Cookies from "js-cookie";

//swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import { Loader } from "@/subframe/components/Loader";
import DirectInstallPWA from "./DirectInstallPWA";
import ChatSendNewMessage from "./ChatSendNewMessage";
import {
  clearAttioParamsFromUrl,
  findRecipientByAttioPhone,
  getAttioPhoneForSearch,
  isAttioSearchInProgress,
  markAttioSearchComplete,
  markAttioSearchInProgress,
  resetAttioSession,
} from "../Utils/attioDeepLink";


function Messenger() {
  const { currentRecipient, selectedLabels } = useSelector(
    (state: RootState) => state.messenger
  );
  const { labels } = useSelector((state: RootState) => state.auth);
  const role = useSelector(selectRole);
  const userLabels = useSelector(selectUserLabels);
  const [searchTerm, setSearchTerm] = useState(
    () => getAttioPhoneForSearch() ?? ""
  );
  const dispatch = useDispatch<AppDispatch>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const socket = useRef<Socket | null>(null);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [isChatSendNewMessageOpen, setIsChatSendNewMessageOpen] = useState(false);
  const [isLabelCreationOpen, setIsLabelCreationOpen] = useState(false);
  const [labelId, setLabelId] = useState<number>();
  const [isLabelDeleteOpen, setIsLabelDeleteOpen] = useState(false);
  const [isSwitchOn, setIsSwitchOn] = useState<boolean>(
    currentRecipient?.is_ai_assistant ? true : false
  );
  const [isSwitchToAiAssistantPopupOpen, setIsSwitchToAiAssistantPopupOpen] =
    useState(false);
  const [isSwitchToHumanAgentPopupOpen, setIsSwitchToHumanAgentPopupOpen] =
    useState(false);
  // const [isSelectDateAndTimePopupOpen, setSelectDateAndTimePopupOpen] =
  //   useState(false);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [tokenFailed, setTokenFailed] = useState(false);

  // Language/video section state
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'es' | 'pt' | 'fr'>('en');
  const videoUrls: Record<string, string> = {
    en: 'https://www.youtube.com/embed/zB3DtzEqrKQ',
    es: 'https://www.youtube.com/embed/euVoq7BK-LI',
    pt: 'https://www.youtube.com/embed/MO_G7iRVNJY',
    fr: 'https://www.youtube.com/embed/lVvQRENWkM8',
  };

  const { system_user_id } = useSelector(
    (state: RootState) => state.auth
  );

  // Load data when component mounts
  useEffect(() => {
    if (!system_user_id) return;

    const attioPhone = getAttioPhoneForSearch();

    if (attioPhone) {
      if (isAttioSearchInProgress()) {
        setSearchTerm(attioPhone);
        dispatch(fetchLabels());
        return;
      }

      markAttioSearchInProgress();
      setSearchTerm(attioPhone);
      clearAttioParamsFromUrl();

      dispatch(
        getRecipients({
          page: 1,
          searchKeyword: attioPhone,
          status: filter === "unread" ? "unread" : undefined,
        })
      )
        .unwrap()
        .then((payload) => {
          const items = payload?.items ?? [];
          if (items.length === 0) return;

          const match = findRecipientByAttioPhone(items, attioPhone);
          if (!match) return;

          dispatch(setCurrentRecipient(match.recipient));

          const message = match.conversation;
          if (
            message?.phone_number != null &&
            String(message.phone_number) !== "" &&
            String(message.status ?? "").trim().toLowerCase() === "unread"
          ) {
            dispatch(
              setConversationStatus({
                phone_number: message.phone_number,
                status: "read",
              })
            );
            dispatch(
              updateMessageStatus({
                phone_number: message.phone_number,
                status: "read",
              })
            );
          }
        })
        .catch(() => {})
        .finally(() => {
          markAttioSearchComplete();
        });
    } else if (!isAttioSearchInProgress()) {
      resetAttioSession();
      setSearchTerm("");
      dispatch(
        getRecipients({
          page: 1,
          status: filter === "unread" ? "unread" : undefined,
        })
      );
    }

    dispatch(fetchLabels());
  }, [system_user_id, dispatch]);

  const { recipients, loading, recipientsNextPage } = useSelector(
    (state: RootState) => state.messenger
  );
  const { messages } = useSelector((state: RootState) => state.messenger);

  //socket options
  const options = {
    rememberUpgrade: true,
    transports: ["websocket"],
    secure: true,
    rejectUnauthorized: false,
  };

  const currentRecipientRef = useRef(currentRecipient);
  const currentLabelRef = useRef(labels);
  const roleRef = useRef(role);
  const userLabelsRef = useRef(userLabels);

  // Refs for scroll observer so callback always sees latest (avoids stale closure / random stop)
  const loadingRef = useRef(loading);
  const recipientsNextPageRef = useRef(recipientsNextPage);
  const searchTermRef = useRef(searchTerm);
  const selectedLabelsRef = useRef(selectedLabels);
  const filterRef = useRef(filter);
  const lastRequestedPageRef = useRef(0);
  loadingRef.current = loading;
  recipientsNextPageRef.current = recipientsNextPage;
  searchTermRef.current = searchTerm;
  selectedLabelsRef.current = selectedLabels;
  filterRef.current = filter;
  const loadedPages = Math.ceil((recipients?.items?.length ?? 0) / 20);
  if (loadedPages > 0)
    lastRequestedPageRef.current = Math.max(
      lastRequestedPageRef.current,
      loadedPages
    );

  useEffect(() => {
    currentRecipientRef.current = currentRecipient;
  }, [currentRecipient]);

  useEffect(() => {
    currentLabelRef.current = labels;
  }, [labels]);

  useEffect(() => {
    roleRef.current = role;
    userLabelsRef.current = userLabels;
  }, [role, userLabels]);

  useEffect(() => {
    // Only connect if we have a system_user_id
    if (!system_user_id) return;



    socket.current = io("https://chat-notifyer-backend.onrender.com/", {
      ...options,
      auth: {
        user_id: system_user_id
      }
    });

    const handleNewMessage = (payload: Message | Item) => {
      const currentRecipient = currentRecipientRef.current;
      const currentLabels = currentLabelRef.current;
      const isThreadShape =
        payload &&
        typeof payload === "object" &&
        "recipient" in payload &&
        typeof (payload as Item).recipient === "object" &&
        "conversation" in payload;

      if (isThreadShape) {
        const item = payload as Item;
        dispatch(newRecipientWithMessage({
          newRecipient: item,
          currentRecipient,
          system_user_id,
          role: roleRef.current,
          userLabels: userLabelsRef.current,
        }));
        dispatch(newMessageByWebhook({
          message: item.conversation,
          recipient: item.recipient,
          currentRecipient,
          currentLabels,
          system_user_id,
          role: roleRef.current,
          userLabels: userLabelsRef.current,
        }));
      } else {
        dispatch(newMessageByWebhook({
          message: payload as Message,
          currentRecipient,
          currentLabels,
          system_user_id,
          role: roleRef.current,
          userLabels: userLabelsRef.current,
        }));
      }
    };

    const handleNewRecipientWithMessage = (newRecipient: Item) => {
      const currentRecipient = currentRecipientRef.current;
      dispatch(newRecipientWithMessage({
        newRecipient,
        currentRecipient,
        system_user_id,
        role: roleRef.current,
        userLabels: userLabelsRef.current,
      }));
    };

    const handleConnect = () => {
    };

    const handleDisconnect = () => {
    };

    const handleConnectError = () => {
    };

    // Set up event listeners
    socket.current.on("connect", handleConnect);
    socket.current.on("disconnect", handleDisconnect);
    socket.current.on("connect_error", handleConnectError);
    // Do not remove either event: both are required (data structures may overlap but events are distinct).
    socket.current.on("newMessage", handleNewMessage);
    socket.current.on("newRecipientWithMessage", handleNewRecipientWithMessage);

    // Join user room for real-time updates
    socket.current.emit("joinUserRoom", { user_id: system_user_id });

    // Cleanup function
    return () => {
      if (socket.current) {
        socket.current.off("connect", handleConnect);
        socket.current.off("disconnect", handleDisconnect);
        socket.current.off("connect_error", handleConnectError);
        socket.current.off("newMessage", handleNewMessage);
        socket.current.off("newRecipientWithMessage", handleNewRecipientWithMessage);
        socket.current.disconnect();
        socket.current = null;
      }
    };
  }, [system_user_id, dispatch]);

  useEffect(() => {
    if (currentRecipient?.phone_number && system_user_id) {
      dispatch(
        getConversation({
          perPage: 500,
          pageNumber: 0,
          offset: 0,
          phoneNumber: currentRecipient?.phone_number,
        })
      );
      dispatch(
        getScheduledMessages({
          phoneNumber: currentRecipient?.phone_number,
        })
      );
      dispatch(
        getLegacyScheduledMessages({
          phoneNumber: currentRecipient?.phone_number,
        })
      );
    } else {
      dispatch(clearScheduledMessages());
      dispatch(clearLegacyScheduledMessages());
    }
  }, [currentRecipient?.phone_number, dispatch, system_user_id]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.items]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);

    // If search is cleared, fetch normal data immediately
    if (newSearchTerm === "") {
      lastRequestedPageRef.current = 0;
      dispatch(resetRecipients());
      dispatch(
        getRecipients({
          page: 1,
          selectedLabels:
            selectedLabels.length > 0 ? selectedLabels : undefined,
          status: filter === "unread" ? "unread" : undefined,
        })
      );
    }
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchTerm.trim() !== "") {
      dispatch(
        getRecipients({
          page: 1,
          searchKeyword: searchTerm,
          selectedLabels:
            selectedLabels.length > 0 ? selectedLabels : undefined,
          status: filter === "unread" ? "unread" : undefined,
        })
      );
    }
  };

  // Load more: use refs so observer always sees latest state (stable infinite scroll)
  const observer = useRef<IntersectionObserver | null>(null);
  const lastRecipientRef = useCallback((node: HTMLDivElement) => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      const nextPage = recipientsNextPageRef.current;
      const isLoading = loadingRef.current;
      if (nextPage == null || isLoading) return;

      dispatch(
        getRecipients({
          page: nextPage,
          searchKeyword: searchTermRef.current,
          selectedLabels:
            selectedLabelsRef.current.length > 0
              ? selectedLabelsRef.current
              : undefined,
          status: filterRef.current === "unread" ? "unread" : undefined,
        })
      );
    });

    if (node) observer.current.observe(node);
  }, [dispatch]);

  const handleLoadMore = () => {
    const nextPage = lastRequestedPageRef.current + 1;
    lastRequestedPageRef.current = nextPage;
    dispatch(
      getRecipients({
        page: nextPage,
        searchKeyword: searchTermRef.current,
        selectedLabels:
          selectedLabelsRef.current.length > 0
            ? selectedLabelsRef.current
            : undefined,
        status: filterRef.current === "unread" ? "unread" : undefined,
      })
    );
  };

  //handle AI -> Human Agent only
  const handleAiToHuman = () => {
    if (isSwitchOn) {
      if (currentRecipient && currentRecipient.id) {
        const { id, ...rest } = currentRecipient;
        setIsSwitchOn(false);
        dispatch(updateRecipient({ id, ...rest, is_ai_assistant: false }));
        setIsSwitchToHumanAgentPopupOpen(true);
        setTimeout(() => {
          setIsSwitchToHumanAgentPopupOpen(false);
        }, 3000);
      }
    }
  };

  //handle human agent/ai switch
  const handleSwitchChange = () => {
    if (isSwitchOn) {
      // If switching from true → false, show SwitchToHumanAgentPopup for 3 sec
      if (currentRecipient && currentRecipient.id) {
        const { id, ...rest } = currentRecipient;
        setIsSwitchOn(false);
        dispatch(updateRecipient({ id, ...rest, is_ai_assistant: false }));
        setIsSwitchToHumanAgentPopupOpen(true);
        setTimeout(() => {
          setIsSwitchToHumanAgentPopupOpen(false);
        }, 3000);
      }
    } else {
      // If switching from false → true, open AiAssistantPopup
      setIsSwitchToAiAssistantPopupOpen(true);
    }
  };

  const confirmSwitchToAiAssistant = () => {
    if (currentRecipient && currentRecipient.id) {
      const { id, ...rest } = currentRecipient;
      setIsSwitchToAiAssistantPopupOpen(false);
      setIsSwitchOn(true);
      dispatch(updateRecipient({ id, ...rest, is_ai_assistant: true }));
    }
  };

  const cancelSwitchToAiAssistant = () => {
    setIsSwitchToAiAssistantPopupOpen(false);
    setIsSwitchOn(false);
  };

  useEffect(() => {
    if (currentRecipient) {
      setIsSwitchOn(currentRecipient.is_ai_assistant);
    }
  }, [currentRecipient, currentRecipient?.is_ai_assistant]);

  const canPatchConversationStatus = (message: Message | null | undefined) =>
    Boolean(
      message &&
        message.phone_number != null &&
        String(message.phone_number) !== ""
    );

  const isConversationUnread = (message: Message | null | undefined) =>
    String(message?.status ?? "").trim().toLowerCase() === "unread";

  //handle recipient last message status change
  const handleUpdateMessageStatus = (message: Message) => {
    if (canPatchConversationStatus(message) && isConversationUnread(message)) {
      dispatch(
        setConversationStatus({
          phone_number: message.phone_number,
          status: "read",
        })
      );
      dispatch(
        updateMessageStatus({
          phone_number: message.phone_number,
          status: "read",
        })
      );
    }
  };

  // Mark thread as unread (any status except unread; matches single visible menu action)
  const handleMarkAsUnread = (e: React.MouseEvent, message: Message) => {
    e.stopPropagation();
    if (canPatchConversationStatus(message) && !isConversationUnread(message)) {
      dispatch(
        setConversationStatus({
          phone_number: message.phone_number,
          status: "unread",
        })
      );
      dispatch(
        updateMessageStatus({
          phone_number: message.phone_number,
          status: "unread",
        })
      );
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent, message: Message) => {
    e.stopPropagation();
    if (canPatchConversationStatus(message) && isConversationUnread(message)) {
      dispatch(
        setConversationStatus({
          phone_number: message.phone_number,
          status: "read",
        })
      );
      dispatch(
        updateMessageStatus({
          phone_number: message.phone_number,
          status: "read",
        })
      );
    }
  };

  //handle label slider
  const swiperRef = useRef<SwiperType>();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrows = () => {
    if (swiperRef.current) {
      const swiper = swiperRef.current;
      const isStart = swiper.isBeginning;
      const isEnd = swiper.isEnd;

      setShowLeftArrow(!isStart);
      setShowRightArrow(!isEnd);
    }
  };

  // Update arrows when labels change
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
      requestAnimationFrame(() => {
        updateArrows();
      });
    }
  }, [selectedLabels]);

  // Handle label selection from dropdown
  const handleLabelSelect = (label: string) => {
    lastRequestedPageRef.current = 0;
    dispatch(addSelectedLabel(label));
    dispatch(
      getRecipients({
        page: 1,
        searchKeyword: searchTerm,
        selectedLabels: [...selectedLabels, label],
        status: filter === "unread" ? "unread" : undefined,
      })
    );
  };

  // Handle label removal from slider
  const handleLabelRemove = (label: string) => {
    lastRequestedPageRef.current = 0;
    dispatch(removeSelectedLabel(label));
    const updatedLabels = selectedLabels.filter((l) => l !== label);

    // Always reset list and pagination when filter changes, then refetch
    dispatch(resetRecipients());
    if (updatedLabels.length === 0) {
      dispatch(
        getRecipients({
          page: 1,
          searchKeyword: searchTerm,
          status: filter === "unread" ? "unread" : undefined,
        })
      );
    } else {
      dispatch(
        getRecipients({
          page: 1,
          searchKeyword: searchTerm,
          selectedLabels: updatedLabels,
          status: filter === "unread" ? "unread" : undefined,
        })
      );
    }
  };

  const handleSignIn = () => {
    setTokenFailed(false);
    Cookies.remove("auth_token");
    window.location.href = "/sign-up";
  };


  if (tokenFailed)
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 mx-auto">
          <div className="flex flex-col items-center text-center">
            <div className="rounded-full bg-red-100 p-3 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Session Expired
            </h2>

            <p className="text-gray-600 mb-6">
              Your session has expired or the authentication token is invalid.
              Please sign in again to continue.
            </p>

            <div className="mb-6 border-l-4 border-amber-400 bg-amber-50 p-4 rounded">
              <div className="font-medium text-amber-800">Important</div>
              <div className="text-amber-700 text-sm">
                For security reasons, you have been logged out. Any unsaved
                changes may have been lost.
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleSignIn}
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Sign In
              </button>

              {/* <button
                onClick={() => setTokenFailed(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Close
              </button> */}
            </div>
          </div>
        </div>
      </div>
    );

  // if (!isAuthenticated)
  //   return (
  //     <div className="container max-w-none flex h-full w-full flex-col items-center justify-center gap-4 bg-brand-100">
  //     <form onSubmit={handleSubmit} className="flex w-full max-w-[384px] flex-col items-center justify-center gap-7 rounded-lg border border-solid border-neutral-border bg-white px-12 pt-9 pb-8 shadow-md mobile:h-auto mobile:w-full mobile:max-w-[320px] mobile:flex-none">
  //       <div className="flex w-full flex-col items-center gap-4">
  //         <img
  //           className="w-12 flex-none"
  //           src="https://res.cloudinary.com/subframe/image/upload/v1732870928/uploads/4086/khdrdhexuo94bjzfwcz4.svg"
  //           alt="Logo"
  //         />
  //         <div className="flex flex-col items-center gap-2">
  //           <span className="text-heading-2 font-heading-2 text-default-font">
  //             Chat Notifyer
  //           </span>
  //           <span className="text-body font-body text-subtext-color">
  //             User login to Chat Notifyer
  //           </span>
  //         </div>
  //       </div>

  //       {/* Show API error if any */}
  //       {apiError && (
  //         <div className="w-full p-2 bg-red-100 text-red-700 rounded-md text-sm text-center">
  //           {apiError}
  //         </div>
  //       )}

  //       <div className="flex w-full flex-col items-start gap-6">
  //         <div className="flex w-full flex-col items-start gap-4">
  //           <TextField
  //             className="h-auto w-full flex-none"
  //             variant="outline"
  //             label="Username/Email"
  //             helpText={errors.email || ""}
  //             error={!!errors.email}
  //             icon="FeatherUser"
  //           >
  //             <TextField.Input
  //               name="email"
  //               placeholder="Enter your username or email..."
  //               value={formData.email}
  //               onChange={handleChange}
  //             />
  //           </TextField>

  //           <TextField
  //             className="h-auto w-full flex-none"
  //             variant="outline"
  //             label="Password"
  //             helpText={errors.password || ""}
  //             error={!!errors.password}
  //             icon="FeatherLock"
  //             iconRight={showPassword ? "FeatherEyeOff" : "FeatherEye"}
  //             // onIconRightClick={togglePasswordVisibility}
  //           >
  //             <TextField.Input
  //               name="password"
  //               type={showPassword ? "text" : "password"}
  //               placeholder="Enter your secure password..."
  //               value={formData.password}
  //               onChange={handleChange}
  //             />
  //           </TextField>
  //         </div>
  //       </div>

  //       <Button
  //         type="submit"
  //         className="h-10 w-full flex-none"
  //         size="large"
  //         disabled={isLoading}
  //       >
  //         {isLoading ? "Signing in..." : "Sign In"}
  //       </Button>
  //     </form>

  //     <div className="flex w-96 items-center gap-2 mobile:h-auto mobile:w-80 mobile:flex-none">
  //       <div className="flex h-px grow shrink-0 basis-0 flex-col items-center gap-2 bg-subtext-color" />
  //       <span className="text-body font-body text-subtext-color">
  //         Want to login as Admin
  //       </span>
  //       <div className="flex h-px grow shrink-0 basis-0 flex-col items-center gap-2 bg-subtext-color" />
  //     </div>

  //     <Button
  //       className="mobile:w-52"
  //       variant="brand-secondary"
  //       size="large"
  //       onClick={() =>
  //         window.open(
  //           "https://console.notifyer-systems.com",
  //           "_blank",
  //           "noopener,noreferrer"
  //         )
  //       }
  //     >
  //       Login to Console
  //     </Button>
  //   </div>
  //   );
  // if (error) return <div>Error: {error}</div>;

  return (
    <div className="flex h-full w-full flex-col items-start bg-default-background">
      <div className="flex min-h-0 min-w-0 w-full grow shrink-0 basis-0 items-stretch overflow-hidden">
        <div className="flex min-w-[256px] max-w-[384px] shrink basis-0 grow flex-col items-start self-stretch border-r border-solid border-neutral-border">
          {/* Left top */}
          <div className="flex w-full flex-col items-center gap-1 px-4 pt-4 pb-2">
            <div className="flex w-full items-center gap-4">
              <div className="flex grow shrink-0 basis-0 items-center gap-4 px-2 py-2">
                <span className="grow shrink-0 basis-0 text-heading-2 font-heading-2 text-default-font">
                  Chats
                </span>
              </div>
              <DirectInstallPWA />
              <IconButton
                variant="brand-tertiary"
                size="large"
                icon="FeatherMessageSquarePlus"
                onClick={() => setIsChatSendNewMessageOpen(true)}
              />
              <IconButton
                variant="brand-tertiary"
                size="large"
                icon="FeatherSettings"
                onClick={() => setIsSettingOpen(!isSettingOpen)}
              />
            </div>
            <div className="flex w-full flex-col items-center gap-4 px-2 py-2">
              <div className="flex w-full items-center gap-2">
                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  variant="filled"
                  label=""
                  helpText=""
                  icon="FeatherSearch"
                >
                  <TextField.Input
                    className="conveythis-no-translate"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchSubmit}
                  />
                </TextField>
                <SubframeCore.DropdownMenu.Root>
                  <SubframeCore.DropdownMenu.Trigger asChild={true}>
                    <Button
                      variant="neutral-secondary"
                      icon="FeatherFilter"
                    // onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                    >
                      Filter
                    </Button>
                  </SubframeCore.DropdownMenu.Trigger>
                  <SubframeCore.DropdownMenu.Portal>
                    <SubframeCore.DropdownMenu.Content
                      side="bottom"
                      align="start"
                      sideOffset={4}
                      asChild={true}
                    >
                      <DropdownMenu className="max-h-80 overflow-y-auto thin-scrollbar">
                        {labels.length > 0 ? (
                          labels.map((labelItem, index) => (
                            <DropdownMenu.DropdownItem
                              key={index}
                              icon="FeatherTag"
                              className={`${selectedLabels.includes(labelItem.label)
                                ? "pointer-events-none bg-gray-100"
                                : ""
                                }`}
                              onClick={() => handleLabelSelect(labelItem.label)}
                            >
                              <div className="flex items-center justify-between w-full">
                                <span className="conveythis-no-translate">{labelItem.label}</span>
                                {selectedLabels.includes(labelItem.label) && (
                                  <span className="text-gray-500 text-sm">
                                    Selected
                                  </span>
                                )}
                              </div>
                            </DropdownMenu.DropdownItem>
                          ))
                        ) : (
                          <DropdownMenu.DropdownItem
                            icon={null}
                            disabled={true}
                            className="text-neutral-500"
                          >
                            No labels available
                          </DropdownMenu.DropdownItem>
                        )}
                      </DropdownMenu>
                    </SubframeCore.DropdownMenu.Content>
                  </SubframeCore.DropdownMenu.Portal>
                </SubframeCore.DropdownMenu.Root>
              </div>
              {selectedLabels?.length > 0 && (
                <div className="flex w-full items-center justify-center gap-2 z-0">
                  <IconButton
                    className={`h-6 w-5 flex-none ${showLeftArrow
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                      }`}
                    size="small"
                    icon="FeatherChevronLeft"
                    onClick={() => swiperRef.current?.slidePrev()}
                  />

                  <Swiper
                    modules={[Navigation]}
                    onBeforeInit={(swiper) => {
                      swiperRef.current = swiper;
                    }}
                    onSlideChange={updateArrows}
                    onAfterInit={updateArrows}
                    onResize={updateArrows}
                    slidesPerView="auto"
                    spaceBetween={8}
                    className="max-w-[288px] grow shrink-0 basis-0"
                    observer={true}
                    observeParents={true}
                  >
                    {selectedLabels.map((label, index) => (
                      <SwiperSlide key={index} className="!w-auto">
                        <div className="conveythis-no-translate flex items-center gap-1 rounded-md border border-solid border-neutral-border bg-neutral-100 pl-2 pr-1 py-0.5">
                          <span className="whitespace-nowrap text-caption font-caption text-neutral-700">
                            {label}
                          </span>
                          <IconButton
                            size="small"
                            icon="FeatherX"
                            onClick={() => handleLabelRemove(label)}
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  <IconButton
                    className={`h-6 w-5 flex-none ${showRightArrow
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                      }`}
                    size="small"
                    icon="FeatherChevronRight"
                    onClick={() => swiperRef.current?.slideNext()}
                  />
                </div>
              )}
              <div className="flex w-full items-center gap-2">
                <div className="flex grow shrink-0 basis-0 items-center gap-2">
                  <Button
                    variant={`${filter === "all" ? "brand-primary" : "brand-secondary"
                      }`}
                    onClick={() => {
                      setFilter("all");
                      lastRequestedPageRef.current = 0;
                      dispatch(resetRecipients());
                      dispatch(
                        getRecipients({
                          page: 1,
                          searchKeyword: searchTerm,
                          selectedLabels:
                            selectedLabels.length > 0 ? selectedLabels : undefined,
                        })
                      );
                    }}
                  >
                    All
                  </Button>
                  <Button
                    variant={`${filter === "unread" ? "brand-primary" : "brand-secondary"
                      }`}
                    onClick={() => {
                      setFilter("unread");
                      lastRequestedPageRef.current = 0;
                      dispatch(resetRecipients());
                      dispatch(
                        getRecipients({
                          page: 1,
                          status: "unread",
                          searchKeyword: searchTerm,
                          selectedLabels:
                            selectedLabels.length > 0 ? selectedLabels : undefined,
                        })
                      );
                    }}
                  >
                    Unread
                  </Button>

                  {/* <div className="flex w-px flex-none flex-col items-center gap-2 self-stretch bg-neutral-border" /> */}
                </div>
              </div>
            </div>
          </div>
          {/* Left Bottom - no right padding so scrollbar sits at 100% right of threads panel */}
          <div className="relative flex w-full min-h-0 grow shrink-0 basis-0 flex-col items-start gap-4 pl-4 pr-0 py-4">
            <div className="flex w-full min-h-0 flex-1 flex-col overflow-auto custom-scrollbar w-full">
            {/* Chat List UI */}
            <ChatList>
              {recipients && recipients.items && recipients.items.length > 0 ? (
                [...recipients.items]
                  .filter((item) => {
                    if (filter === "unread") {
                      return (
                        item.conversation?.recipient !== "" &&
                        item.conversation?.status === "unread"
                      );
                    }
                    return true; // Show all if filter is "all"
                  })
                  .sort((a, b) => {
                    const timeA = a.recipient.last_message_time
                      ? new Date(a.recipient.last_message_time).getTime()
                      : 0;

                    const timeB = b.recipient.last_message_time
                      ? new Date(b.recipient.last_message_time).getTime()
                      : 0;

                    return timeB - timeA; // Sort in descending order (latest first)
                  })
                  .map((item: Item, index: number, filteredList: Item[]) => (
                    <div
                      key={`${item.recipient.id}-${index}`}
                      ref={
                        index === filteredList.length - 1
                          ? lastRecipientRef
                          : null
                      }
                      className={`flex w-full items-center gap-2 overflow-hidden rounded-md px-3 py-3 group/row ${currentRecipient?.id === item.recipient.id
                        ? "bg-neutral-100"
                        : "bg-default-background"
                        }`}
                      onClick={() => {
                        dispatch(setCurrentRecipient(item.recipient));
                        handleUpdateMessageStatus(item.conversation);
                      }}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-4">
                        <Friend
                          recipient={item.recipient}
                          lastMessage={item.conversation}
                          rightActions={
                            <SubframeCore.DropdownMenu.Root>
                              <SubframeCore.DropdownMenu.Trigger asChild={true}>
                                <IconButton
                                  variant="neutral-secondary"
                                  icon="FeatherMoreVertical"
                                  size="small"
                                  className="shrink-0"
                                  onClick={(e: React.MouseEvent) =>
                                    e.stopPropagation()
                                  }
                                />
                              </SubframeCore.DropdownMenu.Trigger>
                              <SubframeCore.DropdownMenu.Portal>
                                <SubframeCore.DropdownMenu.Content
                                  side="bottom"
                                  align="end"
                                  sideOffset={4}
                                >
                                  <DropdownMenu className="min-w-[160px]">
                                    {item.conversation &&
                                      canPatchConversationStatus(
                                        item.conversation
                                      ) &&
                                      isConversationUnread(
                                        item.conversation
                                      ) && (
                                        <DropdownMenu.DropdownItem
                                          icon="FeatherCheck"
                                          onClick={(e: React.MouseEvent) =>
                                            handleMarkAsRead(
                                              e,
                                              item.conversation!
                                            )
                                          }
                                        >
                                          Mark as read
                                        </DropdownMenu.DropdownItem>
                                      )}
                                    {item.conversation &&
                                      canPatchConversationStatus(
                                        item.conversation
                                      ) &&
                                      !isConversationUnread(
                                        item.conversation
                                      ) && (
                                        <DropdownMenu.DropdownItem
                                          icon="FeatherMail"
                                          onClick={(e: React.MouseEvent) =>
                                            handleMarkAsUnread(
                                              e,
                                              item.conversation!
                                            )
                                          }
                                        >
                                          Mark as unread
                                        </DropdownMenu.DropdownItem>
                                      )}
                                  </DropdownMenu>
                                </SubframeCore.DropdownMenu.Content>
                              </SubframeCore.DropdownMenu.Portal>
                            </SubframeCore.DropdownMenu.Root>
                          }
                        />
                      </div>
                    </div>
                  ))
              ) : loading ? (
                <div className="flex items-center justify-center w-full">
                  <Loader size="medium" />
                </div>
              ) : (
                <div className="flex w-full items-center justify-center px-3 py-3">
                  <span className="text-body font-body text-neutral-700">
                    No chat available
                  </span>
                </div>
              )}
            </ChatList>
            </div>
            {recipients?.items?.length > 0 && (
                <div
                  className="absolute bottom-4 left-1/2 z-[100] flex -translate-x-1/2 justify-center"
                  aria-label="Load more"
                >
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    title="Load more"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white shadow-lg transition-colors hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  >
                    <SubframeCore.Icon
                      name="FeatherChevronDown"
                      className="h-5 w-5 text-default-font"
                    />
                  </button>
                </div>
              )}
            <div
              className={`absolute top-0 left-0 flex max-w-[384px] h-full bg-white grow shrink-0 basis-0 flex-col items-start gap-4 py-4 overflow-auto custom-scrollbar
                     transition-transform duration-300 ease-in-out
                     ${isSettingOpen ? "translate-x-0" : "-translate-x-full"}`}
            >
              <Settings
                setIsSettingOpen={setIsSettingOpen}
                isSettingOpen={isSettingOpen}
                setIsLabelCreationOpen={setIsLabelCreationOpen}
                setIsLabelDeleteOpen={setIsLabelDeleteOpen}
                setLabelId={setLabelId}
              />
            </div>
            <LabelCreation
              open={isLabelCreationOpen}
              setIsLabelCreationOpen={setIsLabelCreationOpen}
            />
            <DeleteLabelPopup
              open={isLabelDeleteOpen}
              setIsLabelDeleteOpen={setIsLabelDeleteOpen}
              labelId={labelId}
            />
            <SwitchToAiAssistantPopup
              open={isSwitchToAiAssistantPopupOpen}
              setIsSwitchToAiAssistantPopupOpen={
                setIsSwitchToAiAssistantPopupOpen
              }
              onConfirm={confirmSwitchToAiAssistant}
              onCancel={cancelSwitchToAiAssistant}
            />
            <SwitchToHumanAgent
              open={isSwitchToHumanAgentPopupOpen}
              setIsSwitchToHumanAgentPopupOpen={
                setIsSwitchToHumanAgentPopupOpen
              }
            />
            <ChatSendNewMessage
              isNewChatOpen={isChatSendNewMessageOpen}
              setIsNewChatOpen={setIsChatSendNewMessageOpen}
            />
            {/* <SelectDateAndTimePopup
                open={isSelectDateAndTimePopupOpen}
                setSelectDateAndTimePopupOpen={setSelectDateAndTimePopupOpen}
              /> */}

            <div className="hidden w-full grow shrink-0 basis-0 flex-col items-center justify-end gap-4">
              <ActionCard
                className="h-auto w-full flex-none"
                icon="FeatherSettings2"
                title="Chat Setting"
                desc="Usability tests, surveys, card sort, and more"
              />
            </div>
          </div>

          {/* Floating Button - Outside scrollable container but inside Chats section */}
          <div className="absolute bottom-4 left-4 z-0">
            <IconButton
              variant="brand-primary"
              size="large"
              icon="FeatherMessageSquarePlus"
              onClick={() => setIsChatSendNewMessageOpen(true)}
              className="rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out hover:scale-110 active:scale-95 bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500"
            />
          </div>

        </div>
        {/* Middle part- chat room */}
        {currentRecipient && currentRecipient.phone_number ? (
          <ChatRoom
            currentRecipient={currentRecipient}
            messages={messages}
            scrollRef={scrollRef}
            // setSelectDateAndTimePopupOpen={setSelectDateAndTimePopupOpen}
            isSwitchOn={isSwitchOn}
            onToggle={handleSwitchChange}
            handleAiToHuman={handleAiToHuman}
          />
        ) : (
          <div className="flex grow shrink-0 basis-0 flex-col items-center justify-center self-stretch border-l border-solid border-neutral-border">
            <img
              className="max-w-[56px] flex-none mt-6 mb-2"
              src="https://res.cloudinary.com/subframe/image/upload/v1732870928/uploads/4086/khdrdhexuo94bjzfwcz4.svg"
              alt="Chat Notifyer Logo"
            />
            <span className="max-w-[576px] whitespace-pre-wrap font-['Montserrat'] text-[14px] font-[500] leading-[24px] text-subtext-color text-center -tracking-[0.015em]">
              {"Chat seamlessly with recipients, manage message templates, and organize conversations with custom labels - plus much more"}
            </span>
            {/* Language/Video Section */}
            <div className="flex w-full flex-col items-center gap-2 mt-4">
              <div className="flex w-full items-center justify-center gap-2">
                <Button
                  size="small"
                  variant={selectedLanguage === 'en' ? undefined : 'brand-secondary'}
                  onClick={() => setSelectedLanguage('en')}
                >
                  English
                </Button>
                <Button
                  size="small"
                  variant={selectedLanguage === 'es' ? undefined : 'brand-secondary'}
                  onClick={() => setSelectedLanguage('es')}
                >
                  Español
                </Button>
                <Button
                  size="small"
                  variant={selectedLanguage === 'pt' ? undefined : 'brand-secondary'}
                  onClick={() => setSelectedLanguage('pt')}
                >
                  Portuguese
                </Button>
                <Button
                  size="small"
                  variant={selectedLanguage === 'fr' ? undefined : 'brand-secondary'}
                  onClick={() => setSelectedLanguage('fr')}
                >
                  French
                </Button>
              </div>
              <div className="flex items-center justify-center overflow-hidden w-full max-w-sm aspect-video bg-black relative rounded-lg">
                <iframe
                  key={selectedLanguage}
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  src={videoUrls[selectedLanguage]}
                  title="Language Demo Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
            {/* Features Grid */}
            <div className="mt-4 flex w-full max-w-4xl flex-col items-center justify-center px-6 mx-auto gap-2">
              <h3 className="text-sm font-semibold text-gray-800">Key Features</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 w-full">
                {/* Feature 1 */}
                <div className="flex items-start gap-2 p-2 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 flex-shrink-0">
                    <SubframeCore.Icon
                      className="text-caption font-caption text-blue-600"
                      name="FeatherCalendar"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <h4 className="text-xs font-semibold text-gray-800 leading-tight">Advanced Scheduling</h4>
                    <p className="text-[10px] leading-tight text-gray-600">Schedule messages with intelligent delay options</p>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="flex items-start gap-2 p-2 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 flex-shrink-0">
                    <SubframeCore.Icon
                      className="text-caption font-caption text-purple-600"
                      name="FeatherZap"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <h4 className="text-xs font-semibold text-gray-800 leading-tight">AI Automation</h4>
                    <p className="text-[10px] leading-tight text-gray-600">Smart automation with human touchpoints</p>
                  </div>
                </div>

                {/* Feature 3 */}
                <div className="flex items-start gap-2 p-2 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 flex-shrink-0">
                    <SubframeCore.Icon
                      className="text-caption font-caption text-green-600"
                      name="FeatherUsers"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <h4 className="text-xs font-semibold text-gray-800 leading-tight">Team Management</h4>
                    <p className="text-[10px] leading-tight text-gray-600">Granular controls and accountability tools</p>
                  </div>
                </div>

                {/* Feature 4 */}
                <div className="flex items-start gap-2 p-2 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 flex-shrink-0">
                    <SubframeCore.Icon
                      className="text-caption font-caption text-orange-600"
                      name="FeatherTag"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <h4 className="text-xs font-semibold text-gray-800 leading-tight">Smart Labels</h4>
                    <p className="text-[10px] leading-tight text-gray-600">Intelligent labeling and filtering system</p>
                  </div>
                </div>

                {/* Feature 5 */}
                <div className="flex items-start gap-2 p-2 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-pink-100 flex-shrink-0">
                    <SubframeCore.Icon
                      className="text-caption font-caption text-pink-600"
                      name="FeatherMessageCircle"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <h4 className="text-xs font-semibold text-gray-800 leading-tight">Message Templates</h4>
                    <p className="text-[10px] leading-tight text-gray-600">Customizable templates for efficiency</p>
                  </div>
                </div>

                {/* Feature 6 */}
                <div className="flex items-start gap-2 p-2 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 flex-shrink-0">
                    <SubframeCore.Icon
                      className="text-caption font-caption text-indigo-600"
                      name="FeatherRefreshCw"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <h4 className="text-xs font-semibold text-gray-800 leading-tight">Real-time CRM Sync</h4>
                    <p className="text-[10px] leading-tight text-gray-600">Auto-sync all messages to your CRM</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Download App Banner */}
            <div className="mt-4 flex flex-col items-center gap-1">
              <div className="flex gap-4">
                <a
                  href="https://apps.apple.com/es/app/notifyer-by-whatsable/id6743722183?l=en-GB"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download on the App Store"
                    className="h-10"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.chatmobile.chat_mobile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Get it on Google Play"
                    className="h-10"
                  />
                </a>
              </div>
              <span className="text-sm font-semibold text-gray-800">Download our app to answer on the go.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Messenger;
